import { pick } from 'lodash';
import { API_GATEWAY_URL } from '../constants/config';
import { InspectionAreaSheetSeedFieldKeys, MutableInspectionAreaFieldKeys, } from '../interfaces/inspectionAreas';
import { getSignedUrlForUploadFile, getSignedUrlForUploadMeshFile, uploadFile } from './files';
import { patchInspectionSheet } from './inspectionSheet';
import { withStandardHandlers } from './utils';
export const GET_INSPECTION_AREAS_API_URL = (projectId) => `${API_GATEWAY_URL}/projects/${projectId}/inspection-areas`;
export const GET_INSPECTION_AREA_API_URL = (projectId, inspectionAreaId) => `${GET_INSPECTION_AREAS_API_URL(projectId)}/${inspectionAreaId}`;
/**
 * Get all inspection areas for a project
 * @param accessToken - The access token for authentication.
 * @param projectId - The project ID.
 */
export const getInspectionAreas = async (accessToken, projectId) => {
    const doFetch = async (allInspectionAreas, lastEvaluatedKey) => {
        const params = new URLSearchParams({ limit: '1000' });
        if (lastEvaluatedKey) {
            params.append('last_evaluated_key', JSON.stringify(lastEvaluatedKey));
        }
        const fn = () => {
            return fetch(`${API_GATEWAY_URL}/projects/${projectId}/inspection-areas?${params.toString()}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    responseType: 'json',
                    'X-Authorization': `Bearer ${accessToken}`,
                },
            });
        };
        const response = await withStandardHandlers(fn, 'errors.inspectionArea.fetch', false)();
        allInspectionAreas.push(...response.inspection_areas);
        if (response.last_evaluated_key) {
            return doFetch(allInspectionAreas, response.last_evaluated_key);
        }
        return allInspectionAreas;
    };
    return doFetch([]);
};
/**
 * Get a single inspection area by ID
 * @param accessToken - The access token for authentication.
 * @param projectId - The project ID.
 * @param inspectionAreaId - The inspection area ID.
 */
export const getInspectionArea = (accessToken, projectId, inspectionAreaId) => {
    const fn = () => fetch(`${API_GATEWAY_URL}/projects/${projectId}/inspection-areas/${inspectionAreaId}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            responseType: 'json',
            'X-Authorization': `Bearer ${accessToken}`,
        },
    });
    return withStandardHandlers(fn, 'errors.inspectionArea.fetch')();
};
/**
 * Create a new inspection area
 * @param accessToken - The access token for authentication.
 * @param projectId - The project ID.
 * @param inspectionArea - The inspection area details to create, optionally including
 *   sheet-seed fields (creator/witness names and dates, construction properties).
 */
export const postInspectionArea = (accessToken, projectId, inspectionArea) => {
    const fn = () => fetch(`${API_GATEWAY_URL}/projects/${projectId}/inspection-areas`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            responseType: 'json',
            'X-Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(pick(inspectionArea, [...MutableInspectionAreaFieldKeys, ...InspectionAreaSheetSeedFieldKeys])),
    });
    return withStandardHandlers(fn, 'errors.inspectionArea.create')().then((response) => response.inspection_area);
};
/**
 * Update an existing inspection area
 * @param accessToken - The access token for authentication.
 * @param projectId - The project ID.
 * @param inspectionAreaId - The inspection area ID to update.
 * @param inspectionArea - The inspection area details to update.
 */
export const patchInspectionArea = (accessToken, projectId, inspectionAreaId, inspectionArea) => {
    const fn = () => fetch(`${API_GATEWAY_URL}/projects/${projectId}/inspection-areas/${inspectionAreaId}`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            responseType: 'json',
            'X-Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(pick(inspectionArea, MutableInspectionAreaFieldKeys)),
    });
    return withStandardHandlers(fn, 'errors.inspectionArea.update')();
};
/**
 * Update inspection area filename
 * @param accessToken - The access token for authentication.
 * @param projectId - The project ID.
 * @param inspectionAreaId - The inspection area ID.
 * @param filename - The filename to update.
 */
export const patchInspectionAreaFileName = (accessToken, projectId, inspectionAreaId, filename) => {
    const fn = () => fetch(`${API_GATEWAY_URL}/projects/${projectId}/inspection-areas/${inspectionAreaId}`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            responseType: 'json',
            'X-Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ filename }),
    });
    return withStandardHandlers(fn, 'errors.inspectionArea.update')();
};
/**
 * Delete an inspection area
 * @param accessToken - The access token for authentication.
 * @param projectId - The project ID.
 * @param inspectionAreaId - The inspection area ID to delete.
 */
export const deleteInspectionArea = (accessToken, projectId, inspectionAreaId) => {
    const fn = () => fetch(`${API_GATEWAY_URL}/projects/${projectId}/inspection-areas/${inspectionAreaId}`, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
            responseType: 'json',
            'X-Authorization': `Bearer ${accessToken}`,
        },
    });
    return withStandardHandlers(fn, 'errors.inspectionArea.delete')();
};
/**
 * Upload an area's point cloud and/or mesh to S3 via presigned URLs in parallel, then —
 * when a point cloud was uploaded — attach its filename to the area, which triggers
 * backend processing.
 *
 * @param accessToken - The access token for authentication.
 * @param projectId - The project ID.
 * @param inspectionAreaId - The area the files belong to.
 * @param files - The files and optional progress callback.
 * @returns The area updated with its point-cloud filename, or `null` when no point cloud
 *   was uploaded (a mesh upload alone doesn't change the area record).
 */
const uploadInspectionAreaFiles = async (accessToken, projectId, inspectionAreaId, { pcdFile, meshFile, onProgress }) => {
    const totalFiles = (pcdFile ? 1 : 0) + (meshFile ? 1 : 0);
    if (totalFiles === 0)
        return null;
    let completedFiles = 0;
    const reportProgress = () => {
        completedFiles += 1;
        onProgress?.(Math.round((completedFiles / totalFiles) * 100));
    };
    // Prefix a timestamp and strip characters S3/the backend can't handle.
    const timestamp = new Date().toISOString().replace(/[-:]/g, '').replace(/\..+/, '').replace('T', '_');
    const pcdFileName = pcdFile ? `${timestamp}_${pcdFile.name.replace(/[^\p{L}\p{N}.-]/gu, '_')}` : null;
    const pcdContentType = pcdFile?.type || 'text/plain';
    await Promise.all([
        pcdFile && pcdFileName
            ? (async () => {
                const pcdSignedUrl = await getSignedUrlForUploadFile(accessToken, pcdFileName, pcdContentType);
                await uploadFile(pcdSignedUrl.url, pcdContentType, pcdFile);
                reportProgress();
            })()
            : Promise.resolve(),
        meshFile
            ? (async () => {
                const meshSignedUrl = await getSignedUrlForUploadMeshFile(accessToken, projectId, inspectionAreaId, meshFile.name);
                await uploadFile(meshSignedUrl.url, 'application/zip', meshFile);
                reportProgress();
            })()
            : Promise.resolve(),
    ]);
    if (!pcdFileName)
        return null;
    return patchInspectionAreaFileName(accessToken, projectId, inspectionAreaId, pcdFileName);
};
/**
 * Create an inspection area together with its (optional) files: create the area record,
 * then upload any given files (see {@link uploadInspectionAreaFiles}). Both files are
 * optional; with neither, this is a plain create. If any step after creation fails, the
 * just-created area is deleted (best effort) so a failed upload doesn't leave an
 * orphaned area behind, and the original error is rethrown.
 *
 * @param accessToken - The access token for authentication.
 * @param projectId - The project ID.
 * @param params - The area fields, optional files, and optional progress callback.
 * @returns The inspection area (with its uploaded point cloud attached, if one was given).
 */
export const createInspectionAreaWithFiles = async (accessToken, projectId, { inspectionArea, pcdFile, meshFile, onProgress }) => {
    const created = await postInspectionArea(accessToken, projectId, inspectionArea);
    try {
        const uploaded = await uploadInspectionAreaFiles(accessToken, projectId, created.inspection_area_id, {
            pcdFile,
            meshFile,
            onProgress,
        });
        return uploaded ?? created;
    }
    catch (error) {
        try {
            await deleteInspectionArea(accessToken, projectId, created.inspection_area_id);
        }
        catch {
            // Cleanup is best effort — surface the original failure, not the cleanup's.
        }
        throw error;
    }
};
/**
 * Update an inspection area together with its (optional) files: patch the area record
 * (and, when given, its inspection sheet) in parallel, then upload any given replacement
 * files (see {@link uploadInspectionAreaFiles}).
 *
 * @param accessToken - The access token for authentication.
 * @param projectId - The project ID.
 * @param params - The area fields, optional sheet fields, optional files, and optional
 *   progress callback.
 * @returns The updated inspection area (with its uploaded point cloud attached, if one
 *   was given).
 */
export const updateInspectionAreaWithFiles = async (accessToken, projectId, { inspectionAreaId, inspectionArea, sheet, pcdFile, meshFile, onProgress }) => {
    const [patched] = await Promise.all([
        patchInspectionArea(accessToken, projectId, inspectionAreaId, inspectionArea),
        sheet
            ? patchInspectionSheet(accessToken, projectId, inspectionAreaId, sheet.inspection_sheet_id, sheet)
            : Promise.resolve(null),
    ]);
    const uploaded = await uploadInspectionAreaFiles(accessToken, projectId, inspectionAreaId, {
        pcdFile,
        meshFile,
        onProgress,
    });
    return uploaded ?? patched;
};
/**
 * Get the signed URLs for every file in an inspection area's processed mesh bundle.
 *
 * Paginates through the bundle listing (500 keys per page) and merges every page into a single
 * `{ path -> signed URL }` map. The `manifest.json` entry (when present) points at the bundle
 * manifest describing each mesh and its LODs.
 *
 * @param accessToken Access token for authentication.
 * @param projectId Project ID.
 * @param inspectionAreaId Inspection area ID.
 */
export const getMeshBundleFiles = async (accessToken, projectId, inspectionAreaId) => {
    const doFetch = async (allFiles, token) => {
        const url = new URL(`${GET_INSPECTION_AREA_API_URL(projectId, inspectionAreaId)}/mesh-bundle-files`);
        url.searchParams.append('maxKeys', '500');
        if (token) {
            url.searchParams.append('nextToken', token);
        }
        const fn = () => fetch(url.toString(), {
            method: 'GET',
            headers: {
                'X-Authorization': `Bearer ${accessToken}`,
            },
        });
        const data = await withStandardHandlers(fn, 'errors.inspectionArea.fetch', false)();
        // Merge files from this page
        Object.assign(allFiles, data.files);
        if (data.nextToken) {
            return doFetch(allFiles, data.nextToken);
        }
        return allFiles;
    };
    return doFetch({});
};
/**
 * Fetch and parse an inspection area's mesh bundle manifest from its signed URL.
 *
 * @param url Signed URL of the manifest file.
 */
export function getMeshFileManifest(url) {
    const fn = () => fetch(url);
    return withStandardHandlers(fn, 'errors.inspectionArea.fetch', false)();
}
